import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ScanLine, Plus, RefreshCw, FileBox, Trash2, Search } from "lucide-react";
import { type ColumnDef } from "@tanstack/react-table";
import { DataTable } from "../components/ui/DataTable";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { Input } from "../components/ui/Input";
import { formatDate } from "../lib/utils";
import { useAppStore } from "../stores/appStore";
import { useAuthStore } from "../stores/authStore";
import { canAdminOrg } from "../lib/permissions";
import {
  type PointCloudListItem,
  deletePointCloud,
  listPointClouds,
} from "../lib/pointCloudsApi";
import { UploadPointCloudModal } from "../components/point-clouds/UploadPointCloudModal";

export default function PointClouds() {
  const navigate = useNavigate();
  const { selectedOrgId, selectedSiteId } = useAppStore();
  const { user, organizations } = useAuthStore();
  const isAdmin = canAdminOrg(user, organizations, selectedOrgId);

  const [clouds, setClouds] = useState<PointCloudListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showUpload, setShowUpload] = useState(false);
  const [search, setSearch] = useState("");
  // Tracks which row the user clicked Open on so the button shows a spinner
  // while React unmounts this page and the viewer's API calls + Potree
  // download begin. requestAnimationFrame gives the browser a chance to
  // paint the spinner before navigate() blows the page away.
  const [openingId, setOpeningId] = useState<string | null>(null);

  function openCloud(id: string) {
    if (openingId) return;
    setOpeningId(id);
    requestAnimationFrame(() => navigate(`/point-clouds/${id}`));
  }

  const fetchClouds = useCallback(async () => {
    if (!selectedSiteId) {
      setClouds([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const res = await listPointClouds({
        virtual_site_id: selectedSiteId,
        page,
        page_size: 20,
      });
      setClouds(res.items || []);
      setTotalPages(res.total_pages || 1);
      setTotal(res.total || 0);
    } catch {
      setClouds([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSiteId, page]);

  useEffect(() => {
    fetchClouds();
  }, [fetchClouds]);

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Delete point cloud "${name}"? This removes all files from S3.`)) return;
    try {
      await deletePointCloud(id);
      fetchClouds();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      alert(e.response?.data?.detail || "Delete failed");
    }
  }

  const columns: ColumnDef<PointCloudListItem, unknown>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/15">
            <ScanLine className="h-4 w-4 text-blue-400" />
          </div>
          <div>
            <p className="font-medium text-surface-100">{row.original.name}</p>
            {row.original.description && (
              <p className="text-xs text-surface-500">{row.original.description}</p>
            )}
          </div>
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const s = row.original.status;
        const variant =
          s === "ready" ? "success" : s === "error" ? "danger" : "warning";
        return <Badge variant={variant}>{s}</Badge>;
      },
    },
    {
      accessorKey: "point_count",
      header: "Points",
      cell: ({ row }) => (
        <span className="text-surface-400 text-sm">
          {row.original.point_count != null
            ? row.original.point_count.toLocaleString()
            : "—"}
        </span>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ row }) => (
        <span className="text-surface-400 text-sm">
          {formatDate(row.original.created_at)}
        </span>
      ),
    },
    {
      id: "actions",
      header: "ACTIONS",
      cell: ({ row }) => {
        const opening = openingId === row.original.id;
        const ready = row.original.status === "ready";
        return (
          <div className="flex items-center justify-start gap-1.5">
            <Button
              variant="secondary"
              size="sm"
              isLoading={opening}
              disabled={!!openingId && !opening}
              onClick={(e) => {
                e.stopPropagation();
                openCloud(row.original.id);
              }}
              title={!ready ? "Viewer becomes available once extraction completes" : undefined}
            >
              {opening ? "Opening…" : "Open"}
            </Button>
            {isAdmin && (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(row.original.id, row.original.name);
                }}
              >
                <Trash2 className="h-4 w-4 text-red-400" />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Point Clouds</h1>
          <p className="text-sm text-surface-400 mt-1">
            Potree v2 scans — upload a .zip, place asset tags, measure distances
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-64">
            <Input
              placeholder="Search point clouds..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              icon={<Search className="h-4 w-4" />}
            />
          </div>
          <Button variant="secondary" size="icon" onClick={fetchClouds}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          {isAdmin && (
            <Button
              onClick={() => setShowUpload(true)}
              disabled={!selectedSiteId}
              title={!selectedSiteId ? "Select a site first" : undefined}
            >
              <Plus className="h-4 w-4" />
              Upload Point Cloud
            </Button>
          )}
        </div>
      </div>

      {!selectedSiteId ? (
        <div className="rounded-xl border border-surface-700/50 bg-surface-900/40 px-6 py-12 text-center">
          <FileBox className="mx-auto h-10 w-10 text-surface-600" />
          <p className="mt-3 text-sm text-surface-300">
            Select a virtual site from the top bar to list its point clouds.
          </p>
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={clouds}
          isLoading={isLoading}
          enableSearch={false}
          searchValue={search}
          onSearchChange={setSearch}
          emptyMessage={
            isAdmin
              ? "No point clouds yet. Click 'Upload Point Cloud' to add the first one."
              : "No point clouds for this site yet."
          }
          page={page}
          totalPages={totalPages}
          total={total}
          onPageChange={setPage}
          onRowClick={(row) => {
            if (row.status === "ready") openCloud(row.id);
          }}
        />
      )}

      {selectedSiteId && (
        <UploadPointCloudModal
          isOpen={showUpload}
          onClose={() => setShowUpload(false)}
          virtualSiteId={selectedSiteId}
          onCreated={fetchClouds}
        />
      )}
    </motion.div>
  );
}
